import React, { createContext, useState, useEffect, useContext } from 'react';
import { authService } from '../services/authService';

// Create the context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false); // Changed default to false
  const [error, setError] = useState(null);

  // Check if user is logged in on mount - but only if there's evidence of a session
  useEffect(() => {
    const checkUser = async () => {
      // Only check if there's a stored session indicator
      const hasStoredSession = localStorage.getItem('appwrite_session');
      
      if (!hasStoredSession) {
        // No stored session, so user is definitely not logged in
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser); // This will be null for unauthenticated users
      } catch (err) {
        // This should now only trigger for unexpected errors
        console.error('Auth check error:', err);
        setError('Failed to get current user');
        setUser(null);
        // Clear invalid session
        localStorage.removeItem('appwrite_session');
      } finally {
        setLoading(false);
      }
    };

    checkUser();
  }, []);

  // Register a new user (without password)
  const register = async (email, name, phone) => {
    try {
      setLoading(true);
      setError(null);
      const newUser = await authService.register(email, name, phone); // ✅ Capture the return value
      setUser(newUser); // ✅ Set the user from registration response
      return newUser; // ✅ Return the new user
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Log in user
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      await authService.login(email, password);
      const user = await authService.getCurrentUser();
      setUser(user);
      return user;
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Log out user
  const logout = async () => {
    try {
      setLoading(true);
      setError(null);
      await authService.logout();
      setUser(null);
      // Clear the session flag
      localStorage.removeItem('appwrite_session');
    } catch (err) {
      console.error('Logout error:', err);
      setError(err.message || 'Logout failed');
      // Even if logout fails, clear local state
      setUser(null);
      localStorage.removeItem('appwrite_session');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update user preferences
  const updatePrefs = async (prefs) => {
    try {
      setLoading(true);
      setError(null);
      await authService.updatePrefs(prefs);
      const updatedUser = await authService.getCurrentUser();
      setUser(updatedUser);
      return updatedUser;
    } catch (err) {
      console.error('Update prefs error:', err);
      setError(err.message || 'Failed to update preferences');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    register,
    login,
    logout,
    updatePrefs,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};